import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Play, Database, Brain, Search, BarChart, CheckCircle, AlertCircle, Zap, Cpu, Network, Sparkles } from 'lucide-react';
import lot013Data from '../data/lot013-analysis.json';
import HistoricalDocumentationService from '../services/historicalDocumentationService';
import { useLanguage } from '../contexts/LanguageContext';

export const SwarmAnalysis = () => {
  const { t } = useLanguage();
  const [analysisStatus, setAnalysisStatus] = useState('idle');
  const [agentContributions, setAgentContributions] = useState(() => lot013Data?.swarmAgentContributions || []);
  const [searchResults, setSearchResults] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [stats, setStats] = useState(null);
  const [realHistoricalData, setRealHistoricalData] = useState(null);

  // Initialize Database
  const initializeDatabase = async () => {
    try {
      setAnalysisStatus('initializing');
      const result = await HistoricalDocumentationService.initializeDatabase();
      setAnalysisStatus(result ? 'initialized' : 'error');
    } catch (error) {
      console.error('Failed to initialize database:', error);
      setAnalysisStatus('error');
    }
  };

  // Store theorem data for Lot 013
  const storeLot013Theorem = async () => {
    try {
      setAnalysisStatus('storing');
      const result = await HistoricalDocumentationService.storeHistoricalDocumentationTheorem(lot013Data);
      setAnalysisStatus(result ? 'stored' : 'error');
    } catch (error) {
      console.error('Failed to store theorem:', error);
      setAnalysisStatus('error');
    }
  };

  // Search for similar theorems
  const searchSimilarTheorems = async () => {
    try {
      setAnalysisStatus('searching');
      const results = await HistoricalDocumentationService.searchSimilarArtifacts(lot013Data.artifact.name);
      setSearchResults(Array.isArray(results) ? results : []);
      setAnalysisStatus('searched');
    } catch (error) {
      console.error('Failed to search theorems:', error);
      setAnalysisStatus('error');
    }
  };

  // Learn patterns from theorems
  const learnPatterns = async () => {
    try {
      setAnalysisStatus('learning');
      const results = await HistoricalDocumentationService.learnPatterns();
      setPatterns([results]);
      setAnalysisStatus('learned');
    } catch (error) {
      console.error('Failed to learn patterns:', error);
      setAnalysisStatus('error');
    }
  };

  // Get database statistics
  const getStatistics = async () => {
    try {
      setAnalysisStatus('loading-stats');
      const results = await HistoricalDocumentationService.getStatistics();
      setStats(results);
      setAnalysisStatus('stats-loaded');
    } catch (error) {
      console.error('Failed to load statistics:', error);
      setAnalysisStatus('error');
    }
  };

  // Run real historical documentation analysis
  const runRealHistoricalAnalysis = async () => {
    try {
      setAnalysisStatus('analyzing-historical');
      const results = await HistoricalDocumentationService.runCompleteAnalysis(lot013Data);
      setRealHistoricalData(results);
      
      // Update the historical documentation agent contribution with real data
      if (results.provenance) {
        const updatedContributions = agentContributions.map(contribution => {
          if (contribution.agentId === 'historical-documentation-agent') {
            return {
              ...contribution,
              ...results.provenance,
              confidence: results.provenance.confidence || contribution.confidence,
              contribution: results.provenance.contribution || contribution.contribution
            };
          }
          return contribution;
        });
        setAgentContributions(updatedContributions);
      }
      
      // Update search results if available
      if (results.searchResults) {
        setSearchResults(Array.isArray(results.searchResults) ? results.searchResults : []);
      }
      
      // Update patterns if available
      if (results.patterns) {
        setPatterns(Array.isArray(results.patterns) ? results.patterns : [results.patterns]);
      }
      
      // Update stats if available
      if (results.stats) {
        setStats(results.stats);
      }
      
      setAnalysisStatus('complete');
    } catch (error) {
      console.error('Failed to run real historical analysis:', error);
      setAnalysisStatus('error');
    }
  };

  // Run complete swarm analysis
  const runCompleteAnalysis = async () => {
    await initializeDatabase();
    await storeLot013Theorem();
    await searchSimilarTheorems();
    await learnPatterns();
    await getStatistics();
    setAnalysisStatus('complete');
  };

  const statusMessages = {
    idle: 'Ready to begin enhanced swarm analysis',
    initializing: 'Initializing Enhanced Database...',
    initialized: 'Enhanced Database initialized',
    storing: 'Storing Lot 013 theorem with learning trajectory...',
    stored: 'Lot 013 theorem stored with learned trajectory',
    searching: 'Searching with enhanced pattern matching...',
    searched: 'Enhanced search results found',
    learning: 'Learning patterns with Self-Optimizing architecture...',
    learned: 'Advanced patterns learned',
    'loading-stats': 'Loading enhanced statistics...',
    'stats-loaded': 'Enhanced statistics loaded',
    'analyzing-historical': 'Running enhanced historical documentation analysis...',
    complete: 'Enhanced swarm analysis complete with learning',
    error: 'An error occurred during enhanced analysis'
  };

  return (
    <section id="swarm-analysis" className="py-20 sm:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" />
      <div className="absolute top-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="glass border-secondary/30 mb-4">
            <Brain className="w-4 h-4 mr-2 inline" />
            {t('swarmAnalysis.badge')}
          </Badge>
          <h2 className="font-serif font-bold mb-6">
            <span className="text-gradient-gold">{t('swarmAnalysis.titlePart1')}</span> {t('swarmAnalysis.titlePart2')}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t('swarmAnalysis.subtitle')}
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t('swarmAnalysis.verifyingDesc')} {lot013Data.artifact.name}
          </p>
        </div>

        {/* Artifact Overview */}
        <Card className="glass border-card-border mb-12">
          <CardHeader>
            <CardTitle className="text-2xl font-serif text-gradient-gold">
              {t('swarmAnalysis.artifactOverview')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">{t('swarmAnalysis.identification')}</h3>
                <p className="text-muted-foreground">
                  <strong>{t('swarmAnalysis.labels.id')}</strong> {lot013Data.artifact.id}<br />
                  <strong>{t('swarmAnalysis.labels.name')}</strong> {lot013Data.artifact.name}<br />
                  <strong>{t('swarmAnalysis.labels.dynasty')}</strong> {lot013Data.artifact.dynasty}<br />
                  <strong>{t('swarmAnalysis.labels.period')}</strong> {lot013Data.artifact.period}
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">{t('swarmAnalysis.authStatus')}</h3>
                <div className="flex items-center gap-2">
                  {lot013Data.finalAuthentication.verdict === 'Authenticated' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className="font-semibold">
                    {t('swarmAnalysis.status.authenticated')}
                  </span>
                  <Badge variant="secondary">
                    {lot013Data.finalAuthentication.overallConfidence}{t('swarmAnalysis.confidence')}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>



        {/* Agent Contributions */}
        <Card className="glass border-card-border mb-12">
          <CardHeader>
            <CardTitle className="text-2xl font-serif text-gradient-gold flex items-center gap-2">
              <Database className="w-6 h-6" />
              {t('swarmAnalysis.agentContributions')}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {t('swarmAnalysis.agentContributionsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {agentContributions.map((contribution, index) => {
                // Get translations with fallback to JSON data
                const agentRole = t(`swarmAnalysis.agents.${contribution.agentId}.role`) !== `swarmAnalysis.agents.${contribution.agentId}.role`
                  ? t(`swarmAnalysis.agents.${contribution.agentId}.role`)
                  : contribution.role;
                const agentMethodology = t(`swarmAnalysis.agents.${contribution.agentId}.methodology`) !== `swarmAnalysis.agents.${contribution.agentId}.methodology`
                  ? t(`swarmAnalysis.agents.${contribution.agentId}.methodology`)
                  : contribution.methodology;
                const agentResult = t(`swarmAnalysis.agents.${contribution.agentId}.result`) !== `swarmAnalysis.agents.${contribution.agentId}.result`
                  ? t(`swarmAnalysis.agents.${contribution.agentId}.result`)
                  : contribution.contribution;

                return (
                  <div key={index} className="glass rounded-lg p-4 border border-primary/20">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-foreground">{contribution.agentId}</h3>
                        <p className="text-muted-foreground mt-1">{agentResult}</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          <strong>{t('swarmAnalysis.labels.role')}</strong> {agentRole}<br />
                          <strong>{t('swarmAnalysis.labels.methodology')}</strong> {agentMethodology}
                        </p>
                        {realHistoricalData && contribution.agentId === 'historical-documentation-agent' && (
                          <div className="mt-2 p-2 bg-muted/50 rounded">
                            <p className="text-sm">
                              <strong>{t('swarmAnalysis.realResults')}</strong> {realHistoricalData.provenance?.details?.verificationMethod}
                            </p>
                          </div>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-sm">
                        {contribution.confidence}{t('swarmAnalysis.confidence')}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card className="glass border-card-border mb-12">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold flex items-center gap-2">
                <Search className="w-6 h-6" />
                {t('swarmAnalysis.similarArtifacts')}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {t('swarmAnalysis.similarArtifactsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {searchResults.map((result, index) => (
                  <div key={index} className="glass rounded-lg p-4 border border-primary/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-foreground">{result.match}</h3>
                        <p className="text-muted-foreground text-sm mt-1">{t('swarmAnalysis.labels.id')} {result.id}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="mb-1">
                          {result.similarity}% {t('swarmAnalysis.similarity')}
                        </Badge>
                        <p className="text-sm text-muted-foreground">{result.confidence}{t('swarmAnalysis.confidence')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Learned Patterns */}
        {patterns.length > 0 && (
          <Card className="glass border-card-border mb-12">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold flex items-center gap-2">
                <Brain className="w-6 h-6" />
                {t('swarmAnalysis.learnedPatterns')}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {t('swarmAnalysis.learnedPatternsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {patterns.map((pattern, index) => (
                  <div key={index} className="glass rounded-lg p-4 border border-primary/20">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-foreground">{pattern.pattern}</h3>
                        {pattern.description && (
                          <p className="text-muted-foreground text-sm mt-1">{pattern.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary" className="mb-1">
                          {pattern.confidence}{t('swarmAnalysis.confidence')}
                        </Badge>
                        <p className="text-sm text-muted-foreground">{pattern.successRate}% {t('swarmAnalysis.successRate')}</p>
                        <p className="text-sm text-muted-foreground">{pattern.applications} {t('swarmAnalysis.applications')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statistics */}
        {stats && (
          <Card className="glass border-card-border">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-gradient-gold flex items-center gap-2">
                <BarChart className="w-6 h-6" />
                {t('swarmAnalysis.dbStats')}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {t('swarmAnalysis.dbStatsDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="glass rounded-lg p-4 text-center border border-primary/20">
                  <div className="text-2xl font-serif font-semibold text-gradient-gold">
                    {stats.totalTheorems}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('swarmAnalysis.statLabels.theorems')}</div>
                </div>
                <div className="glass rounded-lg p-4 text-center border border-primary/20">
                  <div className="text-2xl font-serif font-semibold text-gradient-gold">
                    {stats.successRate}%
                  </div>
                  <div className="text-sm text-muted-foreground">{t('swarmAnalysis.statLabels.successRate')}</div>
                </div>
                <div className="glass rounded-lg p-4 text-center border border-primary/20">
                  <div className="text-2xl font-serif font-semibold text-gradient-gold">
                    {stats.storageSize}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('swarmAnalysis.statLabels.storage')}</div>
                </div>
                <div className="glass rounded-lg p-4 text-center border border-primary/20">
                  <div className="text-2xl font-serif font-semibold text-gradient-gold">
                    {stats.agentTypes}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('swarmAnalysis.statLabels.agentTypes')}</div>
                </div>
                <div className="glass rounded-lg p-4 text-center border border-primary/20">
                  <div className="text-2xl font-serif font-semibold text-gradient-gold">
                    {stats.patternCount}
                  </div>
                  <div className="text-sm text-muted-foreground">{t('swarmAnalysis.statLabels.patterns')}</div>
                </div>
                {/* Enhanced Metrics Row */}
                <div className="glass rounded-lg p-4 text-center border border-secondary/20 col-span-2 md:col-span-5 mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-lg font-serif font-semibold text-secondary">
                        {stats.gnnLayers}
                      </div>
                      <div className="text-xs text-muted-foreground">{t('swarmAnalysis.metricLabels.neuralLayers')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-serif font-semibold text-secondary">
                        {stats.attentionMechanisms}
                      </div>
                      <div className="text-xs text-muted-foreground">{t('swarmAnalysis.metricLabels.attentionMech')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-serif font-semibold text-secondary">
                        {stats.patternClusters}
                      </div>
                      <div className="text-xs text-muted-foreground">{t('swarmAnalysis.metricLabels.patternClusters')}</div>
                    </div>
                    <div>
                      <div className="text-lg font-serif font-semibold text-secondary">
                        {stats.trajectoriesProcessed}
                      </div>
                      <div className="text-xs text-muted-foreground">{t('swarmAnalysis.metricLabels.trajectories')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};